import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Form, Table } from 'react-bootstrap';
import Select from 'react-select';
import { FaSearch, FaFileExcel, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import Loading from '~/Components/Loading';
import {
  buscarRazoesProtocoloRemessas,
  consultarProtocoloRemessas,
  exportarProtocoloRemessas,
} from '~/Services/Service';

// W-0883 — Consulta Protocolo de Remessas (Generate > Consulta).
// Migração read-only de ERP/ModuloGenerate/UprotRem.pas: filtros (razão, órgão,
// status, excluídas) + grid + export Excel. Q2 UNION SCPC/SERASA é polimórfico
// (pr.codigo = código-razão no SCPC e = CNPJ no SERASA) — por isso o front envia
// codigo_razao E cnpj_razao quando uma razão é selecionada; o backend aplica cada
// um no seu ramo. Sem char próprio (decisão operador 2026-06-08: espelha GES);
// rota/menu gateiam por char 630 (MigracaoERP) como portão de migração.

const ORGAO_AMBOS = 'AMBOS';
const STATUS_AMBOS = 'AMBOS';

const extrairMensagem = err =>
  err && err.message ? err.message : 'Erro inesperado';

const formatarData = valor => {
  if (!valor) return '—';
  const data = moment(valor);
  return data.isValid() ? data.format('DD/MM/YYYY') : '—';
};

// Colunas do grid (ordem fiel ao .pas). campo = chave snake_case do response.
const COLUNAS = [
  { campo: 'codigo', label: 'Código', tipo: 'numero' },
  { campo: 'razao', label: 'Razão', tipo: 'texto' },
  // DW-0963: CNPJ é alfanumérico (string) — ordenar como texto. Sort numérico
  // (Number()) geraria NaN→0 para CNPJ alfanumérico, colapsando a ordenação.
  { campo: 'cnpj', label: 'CNPJ', tipo: 'texto' },
  { campo: 'orgao', label: 'Órgão', tipo: 'texto' },
  { campo: 'sequencia', label: 'Sequência', tipo: 'numero' },
  { campo: 'dt_envio', label: 'Envio', tipo: 'data' },
  { campo: 'dt_retorno', label: 'Retorno', tipo: 'data' },
  { campo: 'nome_arq', label: 'Arquivo', tipo: 'texto' },
  { campo: 'excluida', label: 'Exc.', tipo: 'texto' },
];

const ProtocoloRemessas = () => {
  const [razoes, setRazoes] = useState([]);
  const [razaoSelecionada, setRazaoSelecionada] = useState(null);
  const [orgao, setOrgao] = useState(ORGAO_AMBOS);
  const [statusRetorno, setStatusRetorno] = useState(STATUS_AMBOS);
  const [mostrarExcluidas, setMostrarExcluidas] = useState(false);

  const [linhas, setLinhas] = useState([]);
  const [pesquisado, setPesquisado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);

  const [ordenacao, setOrdenacao] = useState({ campo: null, asc: true });

  useEffect(() => {
    let ativo = true;
    const carregarRazoes = async () => {
      try {
        const lista = await buscarRazoesProtocoloRemessas();
        if (ativo) setRazoes(lista || []);
      } catch (err) {
        if (ativo) toast.error(extrairMensagem(err));
      }
    };
    carregarRazoes();
    return () => {
      ativo = false;
    };
  }, []);

  // Lookup razão: guarda codigo E cnpj para alimentar ambos os ramos do UNION.
  const opcoesRazao = razoes.map(r => ({
    value: r.codigo,
    cnpj: r.cnpj,
    label: r.razao,
  }));

  const montarPayload = () => ({
    orgao,
    status_retorno: statusRetorno,
    mostrar_excluidas: mostrarExcluidas,
    codigo_razao: razaoSelecionada ? razaoSelecionada.value : null,
    cnpj_razao: razaoSelecionada ? razaoSelecionada.cnpj : null,
  });

  const handleConsultar = async () => {
    setLoading(true);
    try {
      const dados = await consultarProtocoloRemessas(montarPayload());
      const resultado = dados || [];
      setLinhas(resultado);
      setPesquisado(true);
      if (resultado.length === 0) {
        toast.info('Nenhum registro encontrado');
      }
    } catch (err) {
      toast.error(extrairMensagem(err));
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = async () => {
    setExportando(true);
    try {
      const blob = await exportarProtocoloRemessas(montarPayload());
      const nomeArquivo = `protocolo-remessas-${moment().format(
        'YYYY-MM-DD'
      )}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extrairMensagem(err));
    } finally {
      setExportando(false);
    }
  };

  const handleLimpar = () => {
    setRazaoSelecionada(null);
    setOrgao(ORGAO_AMBOS);
    setStatusRetorno(STATUS_AMBOS);
    setMostrarExcluidas(false);
    setLinhas([]);
    setPesquisado(false);
    setOrdenacao({ campo: null, asc: true });
  };

  const ordenarPor = campo => {
    setOrdenacao(atual => {
      if (atual.campo === campo) return { campo, asc: !atual.asc };
      return { campo, asc: true };
    });
  };

  const iconeOrdenacao = campo => {
    if (ordenacao.campo !== campo) return <FaSort className="ml-1 text-muted" />;
    return ordenacao.asc ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  // Sorting client-side: numérico, data (epoch) e texto.
  const valorOrdenacao = (item, coluna) => {
    const bruto = item[coluna.campo];
    if (coluna.tipo === 'numero') {
      const numero = Number(bruto);
      return isNaN(numero) ? 0 : numero;
    }
    if (coluna.tipo === 'data') {
      if (!bruto) return 0;
      const data = moment(bruto);
      return data.isValid() ? data.valueOf() : 0;
    }
    return bruto ? String(bruto).toLowerCase() : '';
  };

  const linhasOrdenadas = () => {
    if (!ordenacao.campo) return linhas;
    const coluna = COLUNAS.find(c => c.campo === ordenacao.campo);
    if (!coluna) return linhas;
    const copia = [...linhas];
    copia.sort((a, b) => {
      const va = valorOrdenacao(a, coluna);
      const vb = valorOrdenacao(b, coluna);
      if (va < vb) return ordenacao.asc ? -1 : 1;
      if (va > vb) return ordenacao.asc ? 1 : -1;
      return 0;
    });
    return copia;
  };

  const ocupado = loading || exportando;
  const listaExibida = linhasOrdenadas();

  return (
    <Container fluid className="py-3">
      <HeaderAzul title="Protocolo de Remessas" />

      <Card className="mb-3 mt-3">
        <Card.Body>
          <Row className="mb-2">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="small mb-1">Razão</Form.Label>
                <Select
                  placeholder="Todas as razões credenciadas"
                  options={opcoesRazao}
                  value={razaoSelecionada}
                  onChange={sel => setRazaoSelecionada(sel || null)}
                  isClearable
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col xs={12} md={6}>
              <Form.Label className="small mb-1 mr-3">Órgão:</Form.Label>
              <Form.Check
                inline
                type="radio"
                id="orgao-scpc"
                name="orgao"
                label="SCPC"
                checked={orgao === 'SCPC'}
                onChange={() => setOrgao('SCPC')}
              />
              <Form.Check
                inline
                type="radio"
                id="orgao-serasa"
                name="orgao"
                label="SERASA"
                checked={orgao === 'SERASA'}
                onChange={() => setOrgao('SERASA')}
              />
              <Form.Check
                inline
                type="radio"
                id="orgao-ambos"
                name="orgao"
                label="Ambos"
                checked={orgao === ORGAO_AMBOS}
                onChange={() => setOrgao(ORGAO_AMBOS)}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="small mb-1 mr-3">Status:</Form.Label>
              <Form.Check
                inline
                type="radio"
                id="status-retornados"
                name="status"
                label="Retornados"
                checked={statusRetorno === 'RETORNADOS'}
                onChange={() => setStatusRetorno('RETORNADOS')}
              />
              <Form.Check
                inline
                type="radio"
                id="status-nao-retornados"
                name="status"
                label="Não retornados"
                checked={statusRetorno === 'NAO_RETORNADOS'}
                onChange={() => setStatusRetorno('NAO_RETORNADOS')}
              />
              <Form.Check
                inline
                type="radio"
                id="status-ambos"
                name="status"
                label="Ambos"
                checked={statusRetorno === STATUS_AMBOS}
                onChange={() => setStatusRetorno(STATUS_AMBOS)}
              />
            </Col>
          </Row>

          <Row className="mb-2">
            <Col xs={12}>
              <Form.Check
                type="checkbox"
                id="mostrar-excluidas"
                label="Mostrar remessas excluídas"
                checked={mostrarExcluidas}
                onChange={e => setMostrarExcluidas(e.target.checked)}
              />
            </Col>
          </Row>

          <div className="d-flex">
            <Button
              variant="primary"
              className="mr-2"
              onClick={handleConsultar}
              disabled={ocupado}
            >
              <FaSearch className="mr-1" /> Consultar
            </Button>
            <Button
              variant="success"
              className="mr-2"
              onClick={handleExportar}
              disabled={ocupado}
            >
              <FaFileExcel className="mr-1" /> Exportar Excel
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleLimpar}
              disabled={ocupado}
            >
              Limpar
            </Button>
          </div>
        </Card.Body>
      </Card>

      {loading && <Loading />}

      {!loading && pesquisado && (
        <Card>
          <Card.Body>
            {listaExibida.length === 0 ? (
              <p className="text-muted mb-0">Nenhum registro encontrado</p>
            ) : (
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    {COLUNAS.map(coluna => (
                      <th
                        key={coluna.campo}
                        role="button"
                        onClick={() => ordenarPor(coluna.campo)}
                      >
                        {coluna.label}
                        {iconeOrdenacao(coluna.campo)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {listaExibida.map(item => (
                    <tr
                      key={`${item.orgao}-${item.codigo}-${item.sequencia}`}
                    >
                      <td>{item.codigo}</td>
                      <td>{item.razao}</td>
                      <td>{item.cnpj}</td>
                      <td>{item.orgao}</td>
                      <td>{item.sequencia}</td>
                      <td>{formatarData(item.dt_envio)}</td>
                      <td>{formatarData(item.dt_retorno)}</td>
                      <td>{item.nome_arq}</td>
                      <td>{item.excluida}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default ProtocoloRemessas;
